import { type ExpandedResolvedWishlistEntryModel } from "~/features/wishlist/types/model";
import { type Table } from "@tanstack/react-table";
import { IMG_EMPTY_WISHLIST } from "~/utils/url/staticResources";

import { useCallback } from "react";
import { useListVisibilityWatcher } from "~/common/hooks/useListVisibilityWatcher";
import { useDispatch } from "react-redux";
import { wishlistAfterScrollThunk } from "~/features/wishlist/redux/wishlistSlice";
import { useWishlistQuickEntry } from "~/pages/WishlistPage/useWishlistQuickEntry";
import Checkbox from "~/common/components/Checkbox";

import { styled } from "~/common/mui";
import { WishlistItem } from "~/pages/WishlistPage/components/WishlistDetails/components/WishlistItem/WishlistItem";
import { LinkButton } from "~/common/components/Link";
import classNames from "classnames";
import { InformationPage } from "~/common/components/informationPage/InformationPage";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { useTranslation } from "react-i18next";
import { canSeeAddToCartButton } from "~/utils/features";
import AddIcon from "@mui/icons-material/Add";

const Root = styled("div", {
  shouldForwardProp: (prop) => prop !== "$border",
})<{ $border: boolean }>(({ theme, $border }) => ({
  "&::after": {
    content: '""',
    display: "block",
    marginTop: theme.spacing(8),
    ...($border && {
      borderBottom: `3px solid ${theme.palette.divider}`,
    }),
  },
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    paddingBottom: theme.spacing(4),
  },
}));

const List = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  fontSize: 24,
  margin: `0 ${theme.spacing(4)} 0 0`,
  [theme.breakpoints.up("desktop-s")]: {
    fontSize: 16,
  },
}));

interface Props {
  tableRef: Table<ExpandedResolvedWishlistEntryModel>;
  isCustomer: boolean;
  className?: string;
  makeSetSaleEnabled: (index: number) => (saleEnabled: boolean) => void;
}
export const WishlistDetails = ({ tableRef, isCustomer, className, makeSetSaleEnabled }: Props) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const onAfterScroll = useCallback(
    (oldLastSeen: number, newLastSeen: number) => dispatch(wishlistAfterScrollThunk(oldLastSeen, newLastSeen)),
    [dispatch],
  );

  const rows = tableRef.getCoreRowModel().rows;
  const hasItems = rows.length > 0;
  const entryIds = rows.map((row) => row.original.id);

  const canAddToCart = canSeeAddToCartButton();
  const isSomethingSelectable = rows.some((row) => row.getCanSelect());

  const listRef = useListVisibilityWatcher<HTMLDivElement>(onAfterScroll, ".js-wishlist-entry", entryIds);

  const isAnySelected = tableRef.getIsSomeRowsSelected();
  const isAllSelected = tableRef.getIsAllRowsSelected();

  const canSelectItems = canAddToCart || userIsRegistered();

  const selectAllCbox = {
    checked: isAllSelected,
    disabled: !canSelectItems || !hasItems || !isSomethingSelectable,
    indeterminate: !isAllSelected && isAnySelected,
    onCheck: tableRef.toggleAllRowsSelected,
  };

  const [quickEntryModal, openQuickEntry] = useWishlistQuickEntry();

  if (!hasItems) {
    return (
      <>
        <EmptyWishlist className={className} onCtaClick={openQuickEntry} />
        {quickEntryModal}
      </>
    );
  }

  const quickEntryButton = (
    <LinkButton onClick={openQuickEntry} iconBefore={<AddIcon  />} className="mt-2">
      {t("page.wishlist.button.quickEntry")}
    </LinkButton>
  );

  return (
    <Root className={classNames(className)} $border={isCustomer}>
      {quickEntryModal}
      <Header>
        {quickEntryButton}
        <StyledCheckbox data-test-id={"wishlist-cbox-select-all"} {...selectAllCbox} />
      </Header>

      {hasItems && (
        <List ref={listRef}>
          {rows.map((row, index) => (
            <WishlistItem
              key={row.original.id}
              entryId={row.original.id}
              index={index}
              row={row}
              canAddToCart={canAddToCart}
              setSaleEnabled={makeSetSaleEnabled(index)}
            />
          ))}
        </List>
      )}
      {rows.length > 3 && quickEntryButton}
    </Root>
  );
};

const EmptyWishlist = ({ onCtaClick, className }: { onCtaClick: () => void; className?: string }) => {
  const { t } = useTranslation();
  return (
    <InformationPage.CenterContents className={classNames("pb-30", className)}>
      <InformationPage.Title>{t("page.wishlist.empty.title")}</InformationPage.Title>
      <InformationPage.Image src={IMG_EMPTY_WISHLIST} />
      <InformationPage.CtaButton onClick={onCtaClick}>{t("page.wishlist.button.quickEntry")}</InformationPage.CtaButton>
      <InformationPage.GoHomepageLink />
    </InformationPage.CenterContents>
  );
};
